import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { Chip, CircularProgress, IconButton, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import styled from "styled-components";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";

const SmallChip = styled(Chip)(() => ({
    height: 24,
    fontSize: 12,
    ".MuiChip-label": {
        paddingLeft: 8,
        paddingRight: 8,
    },
}));

const SmallTextField = styled(TextField)(() => ({
    "& .MuiInputBase-root": {
        fontSize: 14,
        padding: "6px 8px",
    },
    "& .MuiInputLabel-root": {
        fontSize: 14,
    },
}));

const RepoChipAutoComplete = ({
    repos,
    groups,
    setSelectedGroups,
    setSelectedRepos,
    getRepos,
    loading,
}) => {
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const options = [
        ...groups.map((group) => ({
            name: group.name,
            type: "groups",
            repos: group.repos.map((repo) => repo),
        })),
        ...repos.map((repo) => ({ ...repo, type: "repo" })),
    ];

    const deleteAction = async (option) => {
        await request(`api/data/repo-groups`, {
            method: "DELETE",
            body: JSON.stringify({ groups: [option.name] }),
        });
        setMessage(`Group deleted successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
        getRepos();
    };

    const handleDeleteGroup = async (event, option) => {
        event.stopPropagation();
        try {
            deleteAction(option);
        } catch (e) {
            setMessage(`Error deleting group. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    return (
        <div>
            <Autocomplete
                multiple
                id="tags-outlined"
                options={options}
                loading={loading}
                getOptionLabel={(option) => option.name}
                filterSelectedOptions
                disableCloseOnSelect
                size="small"
                groupBy={(option) => option.type.toUpperCase()}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderInput={(params) => (
                    <SmallTextField
                        {...params}
                        label="Select repo / group"
                        placeholder="Repos / Groups"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? (
                                        <CircularProgress
                                            color="inherit"
                                            size={20}
                                            style={{ marginRight: 26 }}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <ListItem {...props}>
                        {option.type === "repo" ? (
                            <ListItemText primary={option.name} />
                        ) : (
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <ListItemText
                                    primary={option.name}
                                    secondary={option.repos.join(", ")}
                                    secondaryTypographyProps={{
                                        style: { fontSize: "11px", fontWeight: 200 },
                                    }}
                                />
                                <IconButton
                                    edge="end"
                                    onClick={(event) => handleDeleteGroup(event, option)}
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        )}
                    </ListItem>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <SmallChip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={`${option.type}:${option.name}`}
                        />
                    ))
                }
                onChange={(event, newValue) => {
                    const selectedRepoNames = newValue
                        .filter((option) => option.type === "repo")
                        .map((option) => `${option.hostname}:${option.name}`);
                    const selectedGroupNames = newValue
                        .filter((option) => option.type === "groups")
                        .map((option) => option.name);

                    setSelectedRepos(selectedRepoNames);
                    setSelectedGroups(selectedGroupNames);
                }}
            />
        </div>
    );
};

export default RepoChipAutoComplete;
